// NewArtwork.js

import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { CModal, CModalHeader, CModalBody, CButton, CForm } from '@coreui/react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArtworkService from '../services/Artwork';
import { AuthUserContext } from '../views/session';
import Logger from '../utils/Logger';
const NewArtwork = ({ show, onHide, addArtwork, onSuccess }) => {
    const { authUser } = useContext(AuthUserContext);
    
    const initialFormState = {
        itemType: '',
        itemSubject: '',
        itemMedia: '',
        itemDate: new Date().toISOString().split('T')[0],
        numberOfCopies: '1',
        itemDetail: '',
        itemDescription: '',
        itemSize: '',
        itemPrice: '0',
        itemCategory: '',
        itemTags: '',
        createStripeProduct: false,
    };

    const [formData, setFormData] = useState(initialFormState);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedItemAsset, setSelectedItemAsset] = useState(null);
    const [itemAssetName, setItemAssetName] = useState('');
    const [usdPrice, setUsdPrice] = useState('0');
    const artworkService = new ArtworkService();

    useEffect(() => {
        if (show) {
            setFormData(initialFormState);
            setSelectedFile(null);
            setPreviewUrl('');
            setIsSubmitting(false);
            setSelectedItemAsset(null);
            setItemAssetName('');
        }
    }, [show]);

    useEffect(() => {
        if (!authUser) {
            Logger.debug('No authenticated user found');
            toast.error('Please sign in to create artwork');
            onHide();
        }
    }, [authUser, onHide]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleItemAssetChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedItemAsset(file);
            setItemAssetName(file.name);
        }
    };

    const resetForm = () => {
        setFormData(initialFormState);
        setSelectedFile(null);
        setPreviewUrl('');
        setIsSubmitting(false);
        setSelectedItemAsset(null);
        setItemAssetName('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        try {
            if (!selectedFile) {
                throw new Error('Please select a file');
            }

            if (!authUser || !authUser.uid) {
                toast.error('Please sign in to create artwork');
                window.location.href = '/login';
                return;
            }

            const submitData = new FormData();
            submitData.append('file', selectedFile);
            
            if (selectedItemAsset) {
                submitData.append('itemAsset', selectedItemAsset);
                submitData.append('itemAssetName', selectedItemAsset.name);
                submitData.append('itemAssetType', selectedItemAsset.type || 'application/octet-stream');
            }

            const formattedDate = new Date(formData.itemDate).toISOString().split('T')[0];
            
            const formFields = {
                ...formData,
                itemDate: formattedDate,
                userId: authUser.uid,
                numberOfCopies: formData.numberOfCopies || '1',
                itemPrice: formData.itemPrice || '0',
                itemCategory: formData.itemType,
                itemTags: formData.itemTags || '',
                itemSize: formData.itemSize || '',
                itemImageType: selectedFile.type || 'image/png',
                itemAsset: selectedItemAsset?.name || 'NO_FILE',
                itemAssetType: selectedItemAsset?.type || 'application/octet-stream',
                createStripeProduct: usdPrice > 0,
                usdPrice: usdPrice || '0',
            };

            Logger.info('Form fields before submission:', {
                ...formFields,
                itemAsset: selectedItemAsset ? 'FILE_PRESENT' : 'NO_FILE',
                file: 'FILE_PRESENT'
            });

            Object.entries(formFields).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    submitData.append(key, value.toString());
                }
            });

            for (let pair of submitData.entries()) {
               // Logger.info(pair[0], pair[1] instanceof File ? 'FILE' : pair[1]);
            }
            //Logger.info('Submit data:', submitData);
            const response = await artworkService.createArtwork(submitData);
            //console.log('Artwork creation response:', response);
            if (response && response.nft) {
                toast.success('NFT created successfully!');
                if (addArtwork) {
                    await addArtwork(response.nft);
                }
                if (onSuccess) {
                    onSuccess(response);
                }
                resetForm();
                onHide();
            } else {
                throw new Error('Failed to create artwork - invalid server response');
            }

        } catch (error) {
            console.error('Error creating artwork:', error);
            toast.error(error.message || 'Failed to create artwork');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleTypeChange = (e) => {
        const itemType = e.target.value;
        setFormData(prev => ({
            ...prev,
            itemType: itemType,
            itemCategory: itemType
        }));
    };

    return (
        <CModal 
            visible={show} 
            onClose={() => {
                resetForm();
                onHide();
            }}
            className="new-artwork-modal" 
            size="lg"
        >
            <CModalHeader closeButton>
                <h5 className="modal-title">Add Artwork to MiEROS Blockchain</h5>
            </CModalHeader>
            <CModalBody>
                <CForm onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Type *</label>
                            <select 
                                className="form-select"
                                name="itemType"
                                value={formData.itemType}
                                onChange={handleTypeChange}
                                required
                            >
                                <option value="">Select Type</option>
                                <option value="Software">Software</option>
                                <option value="Hardware">Hardware</option>
                                <option value="Clothing">Clothing</option>
                                <option value="Music">Music</option>
                                <option value="Art">Art</option>
                                <option value="Document">Document</option>
                            </select>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Subject *</label>
                            <select 
                                className="form-select"
                                name="itemSubject"
                                value={formData.itemSubject}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select Subject</option>
                                <option value="Technology">Technology</option>
                                <option value="Science">Science</option>
                                <option value="Entertainment">Entertainment</option>
                                <option value="Education">Education</option>
                            </select>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Media Type *</label>
                            <select 
                                className="form-select"
                                name="itemMedia"
                                value={formData.itemMedia}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select Media</option>
                                <option value="Digital">Digital</option>
                                <option value="Physical">Physical</option>
                                <option value="Hybrid">Hybrid</option>
                            </select>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Release Date *</label>
                            <input
                                type="date"
                                className="form-control"
                                name="itemDate"
                                value={formData.itemDate}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Number of Copies *</label>
                            <input
                                type="number"
                                className="form-control"
                                name="numberOfCopies"
                                value={formData.numberOfCopies}
                                onChange={handleInputChange}
                                min="1"
                                max="100"
                                required
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Size/Duration</label>
                            <input
                                type="text"
                                className="form-control"
                                name="itemSize"
                                value={formData.itemSize}
                                onChange={handleInputChange}
                                placeholder="e.g., 1920x1080, 3:30"
                            />
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Title *</label>
                            <input
                                type="text"
                                className="form-control"
                                name="itemDetail"
                                value={formData.itemDetail}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Description *</label>
                            <textarea
                                className="form-control"
                                name="itemDescription"
                                value={formData.itemDescription}
                                onChange={handleInputChange}
                                rows="3"
                                required
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Initial Price (MiEROS Tokens)</label>
                            <input
                                type="number"
                                className="form-control"
                                name="itemPrice"
                                value={formData.itemPrice}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">USD Price</label>
                            <div className="input-group">
                                <span className="input-group-text">$</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={usdPrice}
                                    onChange={(e) => setUsdPrice(e.target.value)}
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            <small className="mb-2">
                                Enter 0 to only use MiEROS Tokens 
                            </small>
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Image/File *</label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={handleFileChange}
                                accept="image/*,audio/*,video/*,application/*"
                                required
                            />
                            {previewUrl && (
                                <img 
                                    src={previewUrl} 
                                    alt="Preview" 
                                    className="mt-2" 
                                    style={{ maxWidth: '200px' }} 
                                />
                            )}
                        </div>

                        {(formData.itemType === 'Software' || formData.itemType === 'Document') && (
                            <div className="col-12 mb-3">
                                <label className="form-label">Asset File (Required for Software/Document)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={handleItemAssetChange}
                                    accept="application/*,.pdf,.doc,.docx,.txt"
                                    required={formData.itemType === 'Software' || formData.itemType === 'Document'}
                                />
                                {itemAssetName && (
                                    <small className="mb-2">
                                        Selected file: {itemAssetName}
                                    </small>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="text-end mt-4">
                        <CButton
                            color="secondary"
                            onClick={onHide}
                            className="me-2"
                        >
                            Cancel
                        </CButton>
                        <CButton
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Creating...' : 'Create NFT'}
                        </CButton>
                    </div>
                </CForm>
            </CModalBody>
        </CModal>
    );
};

NewArtwork.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    addArtwork: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default NewArtwork;


